/**
 * Add/edit dialog for an inactivity alert. Hour-of-day is a 0-23
 * select with 12h labels for readability; lookback is a curated
 * preset list. Recipients are a checkbox list of caregivers (and
 * the admin themselves) with at least one email on the member doc.
 */

import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/Dialog';
import { useMembers, type MemberWithId } from '@/hooks/useMembers';
import { upsertAlert } from '@/lib/firestore';
import type { AlertWithId } from '@/hooks/useAlerts';

const LOOKBACK_PRESETS = [1, 2, 3, 4, 6, 8, 12, 24];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  aid: string;
  initial: AlertWithId | null;
}

export function AlertFormDialog({ open, onOpenChange, aid, initial }: Props) {
  const [hourOfDay, setHourOfDay] = useState<number>(initial?.hourOfDay ?? 9);
  const [lookbackHours, setLookbackHours] = useState<number>(initial?.lookbackHours ?? 3);
  const [recipientUids, setRecipientUids] = useState<string[]>(initial?.recipientUids ?? []);
  const [enabled, setEnabled] = useState<boolean>(initial?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { members } = useMembers(aid);
  const eligible = members.filter((m) => !!m.email);

  // Re-seed when the dialog opens, NOT when `initial` identity
  // changes mid-edit (a Firestore live update on the alert being
  // edited would otherwise blow away the user's in-progress changes).
  useEffect(() => {
    if (open) {
      setHourOfDay(initial?.hourOfDay ?? 9);
      setLookbackHours(initial?.lookbackHours ?? 3);
      setRecipientUids(initial?.recipientUids ?? []);
      setEnabled(initial?.enabled ?? true);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function toggleRecipient(uid: string) {
    setRecipientUids((prev) =>
      prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid],
    );
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (recipientUids.length === 0) {
      setError('Pick at least one caregiver to email.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await upsertAlert(aid, initial?.id ?? null, {
        hourOfDay,
        lookbackHours,
        recipientUids,
        enabled,
      });
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogTitle>{initial ? 'Edit alert' : 'New alert'}</DialogTitle>
        <DialogDescription className="sr-only">
          Configure when to email caregivers if no button press is detected.
        </DialogDescription>
        <form className="mt-5 flex flex-col gap-5" onSubmit={onSubmit}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Check at" htmlFor="alert-hour">
              <select
                id="alert-hour"
                className="h-10 w-full rounded-lg border border-paper-border bg-white px-3 text-sm text-ink"
                value={hourOfDay}
                onChange={(e) => setHourOfDay(Number(e.target.value))}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>
                    {formatHour(h)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="If no press in last" htmlFor="alert-lookback">
              <select
                id="alert-lookback"
                className="h-10 w-full rounded-lg border border-paper-border bg-white px-3 text-sm text-ink"
                value={lookbackHours}
                onChange={(e) => setLookbackHours(Number(e.target.value))}
              >
                {LOOKBACK_PRESETS.map((h) => (
                  <option key={h} value={h}>
                    {h} {h === 1 ? 'hour' : 'hours'}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div>
            <span className="mb-2 block text-sm font-medium text-ink">Email these caregivers</span>
            {eligible.length === 0 ? (
              <p className="text-sm text-ink-soft">
                No caregivers with email on file yet. Invite one from Settings → Caregivers.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {eligible.map((m) => (
                  <li key={m.id}>
                    <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-paper-border accent-accent-600"
                        checked={recipientUids.includes(m.id)}
                        onChange={() => toggleRecipient(m.id)}
                      />
                      <span>{recipientLabel(m)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-paper-border accent-accent-600"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span>Enabled</span>
          </label>

          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || recipientUids.length === 0}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatHour(h: number): string {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

function recipientLabel(m: MemberWithId): string {
  const name = m.displayName || '';
  const email = m.email || '';
  if (name && email) return `${name} · ${email}`;
  if (email) return email;
  if (name) return name;
  return m.id;
}
